import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale';
import format from 'date-fns/format';

import { DocumentDropdowns } from '../../../service/dropdownServices/DocumentDropdowns';
import { FILE_SIZE, DEFAULT_FILE_FORMATS } from 'constants/files';
import { FORMAT_DATE_IN_FORM } from 'constants/time';
import { showErrorMessage } from 'helpers/notifications';
import {
  UPLOAD_ONLY_DOCUMENT_TYPES,
  filterDocumentTypeOptions,
  getDocumentDisplayLabel,
  getDocumentProfile,
  resolveDocumentCategory,
} from 'utils/documentGroups';

import 'react-datepicker/dist/react-datepicker.css';
import './addNewDocument.scss';

registerLocale('ru', ru);

class AddNewDocument extends Component {
  constructor(props) {
    super(props);
    this.state = {
      documentTypeId: '',
      documentName: '',
      document: null,
      validFrom: '',
      validTo: '',
      coverageAmount: '',
      policyNumber: '',
      hasUploadedInSession: false,
      sessionUploadedTypeIds: [],
      dropdowns: {
        documentTypes: [],
      },
      selectedCategory: null,
      popupErrors: false,
    };

    this.fileInput = React.createRef();
    this.validFromPicker = React.createRef();
    this.validToPicker = React.createRef();
  }

  componentDidMount() {
    this.loadData();
  }

  loadData() {
    const { lang } = this.props;
    DocumentDropdowns.getDocumentTypes(lang).then((res) => {
      const documentTypesOptions = res.data.map((el) => ({
          value: el.id,
          label: el.value,
      }));
      this.setState({
        dropdowns: {
          documentTypes: documentTypesOptions,
        },
      });
    });
  }

  handleSubmit = (event) => {
    const { documentTypeId, documentName, document, validFrom, validTo, coverageAmount, policyNumber } = this.state;

    event.preventDefault();

    if (documentTypeId && documentName && document) {
      this.setState({ popupErrors: false });
      let formData = new FormData();
      formData.append('documentTypeId', documentTypeId);
      formData.append('document', document);
      if (validFrom) {
        formData.append('validFrom', validFrom);
      }
      if (validTo) {
        formData.append('validTo', validTo);
      }
      if (coverageAmount) {
        formData.append('coverageAmount', coverageAmount);
      }
      if (policyNumber) {
        formData.append('policyNumber', policyNumber);
      }
      this.props.addClick(formData, this.handleUploadSuccess);
    } else {
      this.setState({ popupErrors: true });
    }
  };

  handleFileChange = (e) => {
    const file = this.fileInput.current.files[0];

    if (file && file.size < FILE_SIZE) {
      const extension = this.fileInput.current.files[0].name.split('.').pop();
      if (DEFAULT_FILE_FORMATS.includes(`.${extension}`)) {
        this.setState({
          documentName: this.fileInput.current.files[0].name,
          document: this.fileInput.current.files[0],
        });
      } else {
        showErrorMessage(this.props.t('errors.fileExtension'));
      }
    } else if (file) {
      showErrorMessage(this.props.t('errors.fileSize'));
    }
  };

  handleTypeSelectChange = (item) => {
    this.setState({ documentTypeId: item?.value || item || '' });
  };

  handleCategorySelect = (selectedCategory) => {
    const categoryOptions = this.getAvailableDocumentTypes().filter(
      (option) => resolveDocumentCategory(option.label) === selectedCategory,
    );
    const completedTypeIds = this.getCompletedTypeIds(categoryOptions);
    const nextAvailableOption = categoryOptions.find((option) => !completedTypeIds.has(option.value));
    const shouldAutoSelectDocument = selectedCategory !== 'other';
    const isCategoryCompleted = categoryOptions.length > 0 && categoryOptions.every((option) => completedTypeIds.has(option.value));

    this.setState({
      selectedCategory,
      documentTypeId:
        isCategoryCompleted || !shouldAutoSelectDocument
          ? ''
          : nextAvailableOption?.value || categoryOptions[0]?.value || '',
      popupErrors: false,
    });
  };

  handleFieldChange = (event) => {
    const { name, value } = event.target;

    this.setState({
      [name]: value,
    });
  };

  handleDateChange = (name, date) => {
    this.setState({
      [name]: date ? format(date, FORMAT_DATE_IN_FORM) : '',
    });
  };

  handleUploadSuccess = () => {
    const currentTypeId = this.state.documentTypeId;
    const nextCompletedIds = currentTypeId
      ? new Set([...this.getCompletedTypeIds(), currentTypeId])
      : this.getCompletedTypeIds();
    const filteredDocumentTypes = this.getFilteredDocumentTypes();
    const nextAvailableOption = filteredDocumentTypes.find((option) => !nextCompletedIds.has(option.value));
    const shouldKeepSelectionAfterUpload = this.state.selectedCategory !== 'other';

    this.setState({
      documentName: '',
      document: null,
      validFrom: '',
      validTo: '',
      coverageAmount: '',
      policyNumber: '',
      popupErrors: false,
      hasUploadedInSession: true,
      sessionUploadedTypeIds: currentTypeId
        ? Array.from(new Set([...this.state.sessionUploadedTypeIds, currentTypeId]))
        : this.state.sessionUploadedTypeIds,
      documentTypeId: shouldKeepSelectionAfterUpload ? nextAvailableOption?.value || '' : '',
    });

    if (this.fileInput.current) {
      this.fileInput.current.value = '';
    }
  };

  handleDone = () => {
    this.props.onClose();
  };

  /** Набор категорий и лимитов зависит от роли компании */
  getProfile = () => getDocumentProfile(this.props.companyRole);

  /** Справочник приходит общий, а компании положены только документы её роли */
  getAvailableDocumentTypes = () => filterDocumentTypeOptions(this.state.dropdowns.documentTypes, this.props.companyRole);

  getFilteredDocumentTypes = () => {
    const { selectedCategory } = this.state;

    if (!selectedCategory) {
      return [];
    }

    return this.getAvailableDocumentTypes().filter((option) => resolveDocumentCategory(option.label) === selectedCategory);
  };

  getCategoryCounts = () => {
    const { limits } = this.getProfile();
    const categoryCounts = (this.props.documents || []).reduce((accumulator, document) => {
      const label = document.status || document.file_name || '';
      const categoryKey = resolveDocumentCategory(label);

      accumulator[categoryKey] = (accumulator[categoryKey] || 0) + 1;
      return accumulator;
    }, {});

    this.state.sessionUploadedTypeIds.forEach((typeId) => {
      const option = this.state.dropdowns.documentTypes.find((item) => item.value === typeId);
      if (!option) {
        return;
      }
      const existingDocumentLabels = new Set(
        (this.props.documents || []).map((document) => getDocumentDisplayLabel(document.status || document.file_name || '')),
      );
      if (existingDocumentLabels.has(getDocumentDisplayLabel(option.label))) {
        return;
      }
      const categoryKey = resolveDocumentCategory(option.label);
      categoryCounts[categoryKey] = (categoryCounts[categoryKey] || 0) + 1;
    });

    return Object.fromEntries(
      Object.entries(categoryCounts).map(([categoryKey, count]) => [
        categoryKey,
        Math.min(count, limits[categoryKey] || count),
      ]),
    );
  };

  getSelectedDocumentType = () => {
    const { documentTypeId } = this.state;

    return this.state.dropdowns.documentTypes.find((option) => option.value === documentTypeId) || null;
  };

  getCompletedTypeIds = (options = this.state.dropdowns.documentTypes) => {
    const existingDocumentLabels = new Set(
      (this.props.documents || []).map((document) => getDocumentDisplayLabel(document.status || document.file_name || '')),
    );
    const completedIds = new Set(this.state.sessionUploadedTypeIds);

    options.forEach((option) => {
      if (existingDocumentLabels.has(getDocumentDisplayLabel(option.label))) {
        completedIds.add(option.value);
      }
    });

    return completedIds;
  };

  renderErrors = () => {
    const { documentTypeId, documentName } = this.state;
    const { t } = this.props;
    let errors = [];
    if (!documentTypeId) {
      errors.push(t('documents.addDocument.chooseDocumentType'));
    }

    if (!documentName) {
      errors.push(t('documents.addDocument.uploadDocument'));
    }

    return errors;
  };

  render() {
    const { t } = this.props;
    const {
      selectedCategory,
      documentName,
      validFrom,
      validTo,
      coverageAmount,
      policyNumber,
      hasUploadedInSession,
    } = this.state;
    const { categoryOrder, limits } = this.getProfile();
    const categoryCounts = this.getCategoryCounts();
    const filteredDocumentTypes = this.getFilteredDocumentTypes();
    const selectedDocumentType = this.getSelectedDocumentType();
    const isInsuranceCategory = selectedCategory === 'insurance';
    const isUploadOnly = selectedDocumentType && UPLOAD_ONLY_DOCUMENT_TYPES.has(selectedDocumentType.label);
    const completedTypeIds = this.getCompletedTypeIds(filteredDocumentTypes);

    return (
      <div className="contact__wrapper new-document__wrapper new-document__wrapper--selector">
        <div className="new-document__selector-header">
          <h2 className="popup__title title--yellow new-document__title">
            {t('documents.modal.upload_title')}
          </h2>
          <button className="popup__close-button" type="button" onClick={this.props.onClose} />
        </div>

        <div className="new-document__selector-content">
          {categoryOrder.map((categoryKey) => (
            (() => {
              const isActive = selectedCategory === categoryKey;
              const isCompleted = (categoryCounts[categoryKey] || 0) >= (limits[categoryKey] || 0);

              return (
                <button
                  key={categoryKey}
                  className={`new-document__category${isActive ? ' new-document__category--active' : ''}`}
                  type="button"
                  onClick={() => this.handleCategorySelect(categoryKey)}
                >
                  <span className="new-document__category-title">
                    {t(`documents.modal.categories.${categoryKey}`)}
                  </span>
                  <span
                    className={`new-document__category-count${isActive ? ' new-document__category-count--active' : ''}${isCompleted ? ' new-document__category-count--completed' : ''}`}
                  >
                    {`${categoryCounts[categoryKey] || 0}/${limits[categoryKey] || 0}`}
                  </span>
                </button>
              );
            })()
          ))}

          {selectedCategory && (
            <>
              <div className="new-document__type-list">
                {filteredDocumentTypes.map((option) => {
                  const isCompleted = completedTypeIds.has(option.value);
                  const isActive = this.state.documentTypeId === option.value;

                  return (
                  <button
                    key={option.value}
                    className={`new-document__type-option${isActive ? ' new-document__type-option--active' : ''}${isCompleted ? ' new-document__type-option--completed' : ''}`}
                    type="button"
                    onClick={() => this.handleTypeSelectChange(option.value)}
                  >
                    <span className="new-document__type-radio" />
                    <span className="new-document__type-label">{option.label}</span>
                  </button>
                  );
                })}
              </div>

              {selectedDocumentType && (
                <form className="new-document__details" onSubmit={this.handleSubmit}>
                  <div className="new-document__details-title">{selectedDocumentType.label}</div>

                  <label className="new-document__file-picker">
                    <span className="file__logo" />
                    <span className="new-document__file-picker-text">
                      <span className="new-document__file-picker-title">
                        {documentName || t('documents.modal.choose_file')}
                      </span>
                      <span className="new-document__file-picker-hint">{t('documents.modal.file_hint')}</span>
                    </span>
                    <input
                      className="new-document__native-file-input"
                      onChange={this.handleFileChange}
                      ref={this.fileInput}
                      name="document"
                      type="file"
                      accept={DEFAULT_FILE_FORMATS}
                    />
                  </label>

                  {!isUploadOnly && (
                  <div className="new-document__meta">
                    <div className="new-document__meta-title">{t('documents.modal.additional_data')}</div>
                    <div className="new-document__meta-grid">
                      <div className="new-document__meta-field">
                        <span>{t('documents.modal.valid_from')}</span>
                        <div className="new-document__input-shell new-document__input-shell--date">
                          <DatePicker
                            ref={this.validFromPicker}
                            className="new-document__text-input"
                            name="validFrom"
                            dateFormat="dd.MM.yyyy"
                            locale="ru"
                            calendarStartDay={1}
                            selected={validFrom ? new Date(validFrom) : null}
                            onChange={(date) => this.handleDateChange('validFrom', date)}
                            maxDate={validTo ? new Date(validTo) : null}
                            placeholderText={t('documents.modal.valid_from')}
                            autoComplete="off"
                          />
                          <button
                            type="button"
                            className="calendar__logo new-document__calendar-logo"
                            onClick={() => this.validFromPicker.current?.setOpen(true)}
                          />
                        </div>
                      </div>

                      <div className="new-document__meta-field">
                        <span>{t('documents.modal.valid_to')}</span>
                        <div className="new-document__input-shell new-document__input-shell--date">
                          <DatePicker
                            ref={this.validToPicker}
                            className="new-document__text-input"
                            name="validTo"
                            dateFormat="dd.MM.yyyy"
                            locale="ru"
                            calendarStartDay={1}
                            selected={validTo ? new Date(validTo) : null}
                            onChange={(date) => this.handleDateChange('validTo', date)}
                            minDate={validFrom ? new Date(validFrom) : null}
                            placeholderText={t('documents.modal.valid_to')}
                            autoComplete="off"
                          />
                          <button
                            type="button"
                            className="calendar__logo new-document__calendar-logo"
                            onClick={() => this.validToPicker.current?.setOpen(true)}
                          />
                        </div>
                      </div>

                      {isInsuranceCategory && (
                        <>
                          <label className="new-document__meta-field">
                            <span>{t('documents.modal.coverage_amount')}</span>
                            <div className="new-document__input-shell">
                              <input
                                className="new-document__text-input"
                                name="coverageAmount"
                                type="text"
                                value={coverageAmount}
                                onChange={this.handleFieldChange}
                                placeholder="20 000 USD"
                              />
                            </div>
                          </label>

                          <label className="new-document__meta-field">
                            <span>{t('documents.modal.policy_number')}</span>
                            <div className="new-document__input-shell">
                              <input
                                className="new-document__text-input"
                                name="policyNumber"
                                type="text"
                                value={policyNumber}
                                onChange={this.handleFieldChange}
                                placeholder="1234567890"
                              />
                            </div>
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                  )}

                  <button className="new-document__upload-button" type="submit">
                    {t('documents.modal.upload')}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        <div className="new-document__selector-footer">
          <button className="new-document__cancel-button" type="button" onClick={this.props.onClose}>
            {t('documents.modal.cancel')}
          </button>
          {selectedCategory && (
            <button
              className="new-document__done-button"
              type="button"
              disabled={!hasUploadedInSession}
              onClick={this.handleDone}
            >
              {t('documents.modal.save')}
            </button>
          )}
        </div>

        <ul className="popup__errors-list">
          {this.state.popupErrors
            ? this.renderErrors().map((el, index) => (
                <li className="popup__errors-item" key={index}>
                  <span className="invalid__logo" />
                  {el}
                </li>
              ))
            : ''}
        </ul>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    lang: state.settings.lang,
    companyRole: state.company.companyData?.company_roles || state.profile.profileData?.company_roles,
  };
};

AddNewDocument = connect(mapStateToProps)(AddNewDocument);

export default withTranslation()(AddNewDocument);
